import { isRejected } from '@reduxjs/toolkit';
import React, { ChangeEvent, ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThunkActionDispatch } from 'redux-thunk';
import styled from 'styled-components';
import {
  Button,
  Dropdown,
  Input,
  Notification,
  NotificationContainer,
} from '@mesa-labs/mesa-ui';

import { useDispatch, useSelector } from '../../redux/hooks';
import { cognitoSignIn, updateLogInMethod } from '../../redux/slices/auth';
import { Partners } from '@mesa-labs/mesa-api/dist/types';
import { Header, HeaderContainer, SignInContainer, SubHeader } from '../common/SignInLayout';

const Form = styled.div`
  color: #2E2C34;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

function SignInOpsPage(): ReactElement {
  const dispatch = useDispatch() as ThunkActionDispatch<any>;
  const navigate = useNavigate();
  const loading = useSelector((state) => state.auth.loading);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showLoginNotification, setShowLoginNotification] = useState(false);
  const [loginNotification, setLoginNotification] = useState('');
  const [partnerId, setPartnerId] = useState(Partners.JLL);

  const handleLogin = async () => {
    if (!email || !password) {
      return;
    }

    dispatch(updateLogInMethod('COGNITO'));
    const response = await dispatch(cognitoSignIn({ email, password, partnerId }));

    if (!isRejected(response)) {
      navigate('/', { replace: true });
    } else {
      setLoginNotification(response.error.message);
      setShowLoginNotification(true);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      navigate('/', { replace: true });
    }
  }, [isLoggedIn]);

  return (
    <SignInContainer>
      <NotificationContainer
        portalRootId="notification-root"
        location="bottomRight"
        position="fixed"
        right="32px"
        bottom="32px"
      >
        {
          (!loading && showLoginNotification) && (
            <Notification
              message={loginNotification}
              type="error"
              onClick={() => setShowLoginNotification(false)}
            />
          )
        }
      </NotificationContainer>

      <HeaderContainer>
        <Header>Sign in to your account</Header>
        <SubHeader>Use your Mesa Operations account to access the Partner Portal</SubHeader>
      </HeaderContainer>

      <Form onKeyDown={(evt) => {
        if (evt.key === 'Enter') {
          handleLogin();
        }
      }}>
        <Dropdown
          label="Partner"
          options={[{
            label: 'JLL',
            value: Partners.JLL,
          }, {
            label: 'Newmark',
            value: Partners.NEWMARK,
          },
          ...(process.env.NODE_ENV !== 'production'
            ? [{
              label: 'GHX',
              value: Partners.GHX,
            }, {
              label: 'Demo',
              value: Partners.DEMO,
            }]
            : [])
          ]}
          selectedOption={partnerId}
          onChange={(value) => setPartnerId(value)}
          width="100%"
          marginBottom={8}
        />

        <Input
          onChange={(evt: ChangeEvent<HTMLInputElement>) => setEmail(evt.target.value)}
          type="text"
          value={email}
          label="Email"
        />

        <Input
          onChange={(evt: ChangeEvent<HTMLInputElement>) => setPassword(evt.target.value)}
          type="password"
          value={password}
          label="Password"
        />

        <Button onClick={handleLogin} isLoading={loading} disabled={(!email || !password)} text="Sign In" color="primary" width="100%" />
      </Form>
    </SignInContainer>
  );
}

export default SignInOpsPage;
